import { call, put, select } from 'redux-saga/effects';
import { authMethodsSelector, isAuthenticateSelector } from '../../selectors/login';
import { bridgeSsoSettled, loginSuccessful } from '../../redux/login/actions';
import { getOAuth2Method } from '../../helpers/auth';
import { detectBridgeEntry, getEntryLocation, markNavigationStarted } from '../../auth/ssoBridge';
import { hasNativeHandoff, whenNativeHandoffSettled } from '../../auth/nativeHandoff';
import { loginSilent, startSilentLogin } from '../../auth/authClient';
import { logError } from '../../helpers/bugLogger';
import { userGetRequest } from '../../redux/user/actions';
import { ROUTE_ACCOUNT, ROUTE_LOGIN } from '../../components/Root/Constants';

/**
 * The failures where navigating to the IDP can plausibly succeed where framing
 * it did not. Everything else — `LOGIN_REQUIRED`, a rejected client, a missing
 * endpoint — reproduces identically, and on a tenant with no authorize endpoint
 * the navigating path throws rather than resolving.
 */
const NAVIGATION_MAY_HELP = ['IDP_UNREACHABLE', 'TIMEOUT'];

/**
 * Give up on the bridge and let the user browse normally.
 *
 * On the dedicated `/sso/bridge` route there is nothing to show, so navigate
 * away. When the marker is merely a parameter on a real page, the page is fine
 * as it is — just drop the marker so a reload does not retry.
 *
 * Releases the render hold first: every path through here leaves the browser on
 * this page, so nothing else would take the app off its loader.
 *
 * @param {{ returnTo: string|undefined, isDedicatedRoute: boolean }} entry
 * @returns {Generator} redux-saga effects.
 */
function* fallBackToNormalBrowsing(entry, { authenticated = false } = {}) {
    // Release the render hold first: every path through here leaves the browser
    // on this page, so nothing else will take the app off the blank hold.
    yield put(bridgeSsoSettled());
    if (entry.isDedicatedRoute) {
        // `/sso/bridge` has nothing to render, so it must go somewhere. Sending
        // an already-signed-in user to the login screen costs a second full boot
        // and a flash of a route they are immediately redirected out of.
        markNavigationStarted();
        window.location.href = entry.returnTo || (authenticated ? ROUTE_ACCOUNT : ROUTE_LOGIN);
        return;
    }
    if (!entry.returnTo) return;
    try {
        window.history.replaceState({}, document.title, entry.returnTo);
    } catch (error) {
        // replaceState throws on an opaque origin and past Safari's rate limit.
        // Only cosmetic here — the page below is already the right one.
        console.error('[ssoBridge] could not strip the bridge marker', error);
    }
}

/**
 * Attempt the silent (`prompt=none`) login that completes the mobile-to-web
 * bridge: the IDP already holds an SSO session cookie, so the user is signed in
 * without ever seeing a login screen.
 *
 * Runs at boot, after discovery (which is what tells us the tenant has OAuth2
 * at all) and only while anonymous. Every abandonment path is silent by design:
 * "no SSO session" is the normal outcome for an ordinary web visitor who
 * happened to arrive with the marker, and an error toast there would be noise.
 *
 * @returns {Generator} redux-saga effects.
 */
export function* attemptBridgeSsoSaga() {
    const entry = detectBridgeEntry(getEntryLocation());
    if (!entry) return;

    // Inside the WebView the native side may still be handing over a refresh
    // token through the Flutter bridge (see `components/Root`). Navigating to
    // the IDP now would abort that in flight and throw away a valid session.
    if (hasNativeHandoff()) {
        const hydrated = yield call(whenNativeHandoffSettled);
        if (hydrated) {
            yield* fallBackToNormalBrowsing(entry, { authenticated: true });
            return;
        }
    }

    const isAuthenticated = yield select(isAuthenticateSelector);
    if (isAuthenticated) {
        yield* fallBackToNormalBrowsing(entry, { authenticated: true });
        return;
    }

    const authMethods = yield select(authMethodsSelector);
    if (!getOAuth2Method(authMethods)) {
        // An empty list means discovery produced nothing — most often because it
        // failed outright, since `initAzureAuthSaga` swallows its errors. Only a
        // non-empty list without an oauth2 method is a tenant misconfiguration
        // worth alarming on; reporting the outage as one would bury the signal.
        if (authMethods && authMethods.length) {
            const error = new Error(
                `[ssoBridge] bridge entry but no oauth2 method among: ${authMethods.map(m => m.mode).join(', ')}`
            );
            console.error(error.message);
            logError(error);
        } else {
            console.error('[ssoBridge] bridge entry but discovery produced no auth methods');
        }
        yield* fallBackToNormalBrowsing(entry);
        return;
    }

    // First, without moving: the worker frames the authorize itself, so there is
    // no redirect, no callback document, and nothing to restore afterwards.
    let silent = null;
    try {
        silent = yield call(loginSilent);
    } catch (error) {
        // Reachable, not theoretical: the worker may be unreachable at boot.
        console.error('[ssoBridge] silent login could not start:', error);
        logError(error instanceof Error ? error : new Error(String(error)));
    }

    if (silent && silent.ok) {
        yield put(loginSuccessful());
        yield put(userGetRequest());
        // Marker stripped, page kept: the browser is already where it belongs.
        yield* fallBackToNormalBrowsing(entry, { authenticated: true });
        return;
    }

    if (!silent || !NAVIGATION_MAY_HELP.includes(silent.code)) {
        // Either the IDP holds no session — the ordinary case — or the failure
        // is permanent and navigating would fail the same way. Note that on a
        // tenant with no authorize endpoint `startSilentLogin` *throws*, so
        // falling back indiscriminately would be worse than not trying.
        if (silent && silent.code !== 'LOGIN_REQUIRED') {
            console.error('[ssoBridge] silent login failed:', silent.code, silent.message);
        }
        yield* fallBackToNormalBrowsing(entry);
        return;
    }

    // Worth a second attempt by navigation: a framing refusal arrives as
    // IDP_UNREACHABLE, because browsers fire no error event for one.
    let outcome = 'skipped';
    try {
        outcome = yield call(startSilentLogin, { returnTo: entry.returnTo });
    } catch (error) {
        // Reachable, not theoretical: crypto.subtle is undefined on a non-secure
        // origin and sessionStorage throws when site data is blocked.
        console.error('[ssoBridge] navigating silent login could not start:', error);
        logError(error instanceof Error ? error : new Error(String(error)));
    }

    if (outcome === 'navigating') {
        markNavigationStarted();
        return;
    }
    // Anything else means the browser is staying here, so nothing else will move
    // it off this page.
    yield* fallBackToNormalBrowsing(entry);
}
